import { FC, ReactNode, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

// Base theme configuration for different event types
export interface EventTheme {
  container: string;
  title: string;
  actionButton: string;
  actionButtonHover: string;
}

export const eventThemes = {
  regular: {
    container: "relative w-full h-full p-1 group cursor-pointer",
    title: "text-xs font-medium text-slate-900 truncate",
    actionButton: "h-5 w-5 p-0 text-slate-600 hover:bg-slate-100 hover:text-slate-900 opacity-70 hover:opacity-100 transition-opacity",
    actionButtonHover: "bg-slate-100"
  },
  adventure: {
    container: "relative w-full h-full p-1 group cursor-pointer",
    title: "text-xs font-medium text-white truncate",
    actionButton: "h-5 w-5 p-0 text-emerald-100 hover:bg-emerald-700/30 hover:text-white opacity-70 hover:opacity-100 transition-opacity",
    actionButtonHover: "bg-emerald-700/30"
  }
} as const;

interface BaseEventCardProps {
  theme: keyof typeof eventThemes;
  isMonthView: boolean;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: () => void;
  
  // Primary row content
  icon?: ReactNode;
  title: string;
  actionsMenu: ReactNode;
  
  // Secondary row content
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  
  // Children for additional content
  children?: ReactNode;
}

export const BaseEventCard: FC<BaseEventCardProps> = ({
  theme,
  isMonthView,
  onMouseEnter,
  onMouseLeave,
  icon,
  title,
  actionsMenu,
  leftContent,
  rightContent,
  children
}) => {
  const themeConfig = eventThemes[theme];

  return (
    <div 
      className={themeConfig.container}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Dynamic Responsive Layout */}
      <div className="flex flex-col gap-1.5 h-full justify-center px-1 py-1">
        {/* Primary Row: Icon + Title + Actions */}
        <div className="flex items-center gap-2 min-h-[1.25rem]">
          {/* Icon + Title */}
          <div className="flex-1 min-w-0 flex items-center gap-1.5">
            {icon && <div className="flex-shrink-0">{icon}</div>}
            <div className={themeConfig.title}>
              {title}
            </div>
          </div>

          {/* Actions Menu */}
          <div className="flex-shrink-0">
            {actionsMenu}
          </div>
        </div>

        {/* Secondary Row: Dynamic content */}
        {(leftContent || rightContent) && (
          <div className="flex items-center justify-between gap-2 min-h-[1rem]">
            {/* Left side content */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {leftContent}
            </div>

            {/* Right side content */}
            {rightContent && (
              <div className="flex-shrink-0">
                {rightContent}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Additional children content */}
      {children}
    </div>
  );
};

// Reusable actions menu component
interface EventActionsMenuProps {
  theme: keyof typeof eventThemes;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

export const EventActionsMenu: FC<EventActionsMenuProps> = ({
  theme,
  isOpen,
  onOpenChange,
  children
}) => {
  const themeConfig = eventThemes[theme];

  return (
    <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={themeConfig.actionButton}
        >
          <MoreVertical className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 bg-white">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
